<template>
  <section class="collaborator-details" v-if="details">
    <div class="profile-heading">
      <div class="avatar">
        <fa :icon="['far', 'user-tie-hair']" />
      </div>
      <div>
        <h3>{{ value(details.pessoal.nome) }}</h3>
        <p>{{ value(details.profissional.funcao) }}</p>
        <span>{{ professionalLocation }}</span>
      </div>
      <span class="status" v-if="details.profissional.situacao">
        {{ details.profissional.situacao }}
      </span>
    </div>

    <div class="details-grid">
      <article>
        <header>
          <fa :icon="['far', 'briefcase']" />
          <h4>Dados profissionais</h4>
        </header>
        <dl>
          <div><dt>E-mail institucional</dt><dd>{{ value(details.profissional.emailInstitucional) }}</dd></div>
          <div><dt>Departamento</dt><dd>{{ value(details.profissional.departamento) }}</dd></div>
          <div><dt>Setor</dt><dd>{{ value(details.profissional.setor) }}</dd></div>
          <div><dt>Data de entrada</dt><dd>{{ date(details.profissional.dataEntrada) }}</dd></div>
        </dl>
      </article>

      <article>
        <header>
          <fa :icon="['far', 'user']" />
          <h4>Dados pessoais</h4>
        </header>
        <dl>
          <div><dt>Nome social</dt><dd>{{ value(details.pessoal.nomeSocial) }}</dd></div>
          <div><dt>Data de nascimento</dt><dd>{{ date(details.pessoal.dataNascimento) }}</dd></div>
          <div><dt>E-mail pessoal</dt><dd>{{ value(details.pessoal.email) }}</dd></div>
          <div><dt>Celular</dt><dd>{{ phone(details.pessoal.telefonecelular) }}</dd></div>
          <div><dt>Telefone</dt><dd>{{ phone(details.pessoal.telefonefixo) }}</dd></div>
        </dl>
      </article>

      <article>
        <header>
          <fa :icon="['far', 'id-card']" />
          <h4>Documentos</h4>
        </header>
        <dl>
          <div><dt>CPF</dt><dd>{{ cpf(details.documentos.cpf) }}</dd></div>
          <div><dt>RG</dt><dd>{{ value(details.documentos.rg) }}</dd></div>
          <div><dt>Órgão emissor</dt><dd>{{ value(details.documentos.rgEmissor) }}</dd></div>
          <div><dt>Expedição do RG</dt><dd>{{ date(details.documentos.rgDataExpedicao) }}</dd></div>
          <div><dt>Título eleitoral</dt><dd>{{ value(details.documentos.tituloEleitoralInscricao) }}</dd></div>
          <div><dt>Passaporte</dt><dd>{{ value(details.documentos.passaporteNumero) }}</dd></div>
        </dl>
      </article>

      <article>
        <header>
          <fa :icon="['far', 'location-dot']" />
          <h4>Endereço</h4>
        </header>
        <dl>
          <div class="wide"><dt>Logradouro</dt><dd>{{ addressLine }}</dd></div>
          <div><dt>Bairro</dt><dd>{{ value(details.endereco.bairro) }}</dd></div>
          <div><dt>CEP</dt><dd>{{ cep(details.endereco.cep) }}</dd></div>
          <div class="wide"><dt>Cidade / UF</dt><dd>{{ city }}</dd></div>
        </dl>
      </article>
    </div>

    <footer>
      <button type="button" class="btn btn-secondary" @click="$emit('close')">Fechar</button>
    </footer>
  </section>
</template>

<script>
import { formatDate, maskCEP, maskCPF } from '@/services/utils.service.js'

export default {
  props: {
    details: {
      type: Object,
      required: true
    }
  },
  computed: {
    professionalLocation () {
      const unit = this.details.profissional.unidade
      const acronym = this.details.profissional.siglaUnidade
      return [unit, acronym ? `(${acronym})` : null].filter(Boolean).join(' ') || 'Unidade não informada'
    },
    addressLine () {
      const address = this.details.endereco
      const number = address.numero ? `, ${address.numero}` : ''
      const complement = address.complemento ? ` - ${address.complemento}` : ''
      return address.logradouro ? `${address.logradouro}${number}${complement}` : 'Não informado'
    },
    city () {
      return [this.details.endereco.cidade, this.details.endereco.uf].filter(Boolean).join(' / ') || 'Não informado'
    }
  },
  methods: {
    value ( value ) {
      return value || 'Não informado'
    },
    date ( value ) {
      return value ? formatDate(value) : 'Não informado'
    },
    cpf ( value ) {
      return value ? maskCPF(String(value)) : 'Não informado'
    },
    cep ( value ) {
      return value ? maskCEP(String(value)) : 'Não informado'
    },
    phone ( value ) {
      if (!value) return 'Não informado'

      const digits = String(value).replace(/\D/g, '')
      if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
      if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
      return value
    }
  }
}
</script>

<style lang="scss" scoped>
.collaborator-details {
  color: $color-text;

  .profile-heading {
    display: flex;
    align-items: center;
    gap: $hmg_mid;
    padding-bottom: $hmg_mid;
    border-bottom: $border-component;

    .avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 54px;
      height: 54px;
      flex: 0 0 54px;
      border-radius: 50%;
      color: $color-apollo;
      background: $color-bg;
      font-size: 23px;
    }

    h3, p { margin: 0; }
    h3 { text-transform: capitalize; }
    span { font-size: $font-size-small; opacity: .75; }

    .status {
      margin-left: auto;
      padding: 5px 10px;
      border-radius: 20px;
      color: $color-success;
      background: rgba(31, 167, 91, .1);
      opacity: 1;
    }
  }

  .details-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: $hmg_mid;
    padding: $hmg_mid 0;

    article {
      padding: $hmg_mid;
      border: $border-component;
      border-radius: $border-radius;
      background: $color-bg;

      header {
        display: flex;
        align-items: center;
        gap: $mg_mini;
        margin-bottom: $hmg_small;
        color: $color-apollo;

        h4 { margin: 0; color: $color-text; }
      }
    }

    dl {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: $hmg_small;
      margin: 0;

      div.wide { grid-column: 1 / -1; }
      dt { font-size: $font-size-small; font-weight: normal; opacity: .65; }
      dd { margin: 2px 0 0; overflow-wrap: anywhere; }
    }
  }

  footer {
    display: flex;
    justify-content: flex-end;
    padding-top: $hmg_small;
    border-top: $border-component;
  }
}

@media screen and (max-width: 720px) {
  .collaborator-details {
    .profile-heading { align-items: flex-start; flex-wrap: wrap; }
    .profile-heading .status { margin-left: 70px; }
    .details-grid { grid-template-columns: 1fr; }
    .details-grid dl { grid-template-columns: 1fr; }
    .details-grid dl div.wide { grid-column: auto; }
  }
}
</style>
